import { FetchResult } from "@apollo/client"
import { Box, Button, Checkbox, Stack, Text, VStack } from "@chakra-ui/react"
import { Field, Form, Formik } from "formik"
import { Fragment, MutableRefObject, useRef } from "react"
import {
  HomeRegistrationFragment,
  SoftArchiveRegistrationMutation,
  SoftArchiveRegistrationMutationHookResult,
  useHomeRegistrationsQuery
} from "@pathwright/graphql-client/src/generated"
import Pathicon from "../../pathicon/Pathicon"
import Tip from "../../tips/Tip"
import { useHomeRegistrationVars } from "../registration/HomeRegistrationQueryVars"
import useSoftArchiveRegistration from "../registration/useSoftArchiveRegistration"

type CheckableRegistrationIds = Record<number, boolean>

type MutationsRef = Record<
  number, // registrationId
  SoftArchiveRegistrationMutationHookResult[0]
>

// Since we need to call this hook for a list of registrations, we must
// use a "render" approach rather than attempt to call the hook in a list
// since in edge cases one of the registrations in the list could become
// unmounted (say, if a user was to set aside an included registration from
// by some other means, like a control menu). This could lead to a change in
// the number of hooks called from one render to the next.
function useSoftArchiveRegistrations(
  mutationsRef: MutableRefObject<MutationsRef>
) {
  return async function (registrationIds: CheckableRegistrationIds) {
    const promises = Object.entries(registrationIds).reduce<
      Promise<FetchResult<SoftArchiveRegistrationMutation>>[]
    >((acc, [registratinId, archive]) => {
      if (archive) {
        acc.push(mutationsRef.current[parseInt(registratinId)]())
      }
      return acc
    }, [])

    await Promise.all(promises)
  }
}

type UseSoftArchiveRegistrationProps = {
  registration: HomeRegistrationFragment
  mutationsRef: MutableRefObject<MutationsRef>
}

// Using component to register a mutation.
function UseSoftArchiveRegistration({
  registration,
  mutationsRef
}: UseSoftArchiveRegistrationProps) {
  const [mutate] = useSoftArchiveRegistration(registration)
  mutationsRef.current[registration.id] = mutate
  return null
}

function useInactiveRegistrations(registrations: HomeRegistrationFragment[]) {
  // Query for the registration IDs of the user's inactive registrations.
  const inactiveRegistrationsQuery = useHomeRegistrationsQuery({
    variables: {
      ...useHomeRegistrationVars(),
      // Not seen for 60 days.
      lastSeen: {
        lt: new Date(new Date().getTime() - 1000 * 60 * 60 * 24 * 60)
      }
    },
    // Ignoring inactive registrations when user doesn't have at least 7 registrations.
    skip: registrations.length <= 7
  })

  // Assemble the inactive registrations based on those that are found in the list
  // of provided registrations. This can result in further filtering out inactive
  // registrations in some cases, like when user is registered in Source Cohort
  // but that cohort has been removed from the main registrations list.
  const inactiveRegistrations = (
    inactiveRegistrationsQuery.data?.me?.registrations?.edges?.map(
      (edge) => edge?.node!
    ) || []
  )
    // Filter the registrations list by those registration IDs.
    .filter((inactiveRegistration) =>
      registrations.some(
        (registration) => inactiveRegistration.id === registration.id
      )
    )

  return inactiveRegistrations
}

function HomeSetAsidePathsTip({
  inactiveRegistrations
}: {
  inactiveRegistrations: HomeRegistrationFragment[]
}) {
  const mutationsRef = useRef<MutationsRef>({})
  const handleSoftArchiveRegistrations =
    useSoftArchiveRegistrations(mutationsRef)

  return (
    <Tip
      tip={{
        id: "home-set-aside-paths",
        title: `Clean up your Home Screen`,
        content: (
          <Formik
            initialValues={{} as CheckableRegistrationIds}
            onSubmit={async (values, form) => {
              await handleSoftArchiveRegistrations(values)
              // Clear out form.
              form.resetForm()
            }}
          >
            {(form) => {
              const checkedCount = Object.entries(form.values).filter(
                ([key, value]) =>
                  // Checking agains key and value to ensure we aren't including arbitrary keys in form.values
                  // which seems to be happening for some reason (at least sometimes).
                  inactiveRegistrations.some((r) => `${r.id}` === key && value)
              ).length

              const isAllChecked = checkedCount === inactiveRegistrations.length
              const isIndeterminate = !!checkedCount && !isAllChecked

              return (
                <Form>
                  <VStack>
                    <Text color="inherit" mt={0}>
                      You have {inactiveRegistrations.length} Path(s) you
                      haven’t been active in for the last 60 days:
                    </Text>
                    <Box w="100%">
                      <Checkbox.Root
                        checked={
                          isIndeterminate ? "indeterminate" : isAllChecked
                        }
                        onCheckedChange={(e) =>
                          form.setValues(
                            inactiveRegistrations.reduce<CheckableRegistrationIds>(
                              (acc, r) => {
                                acc[r.id] = !!e.checked
                                return acc
                              },
                              {}
                            )
                          )
                        }
                        colorPalette="whiteAlpha"
                        variant="outline"
                        size="sm"
                      >
                        <Checkbox.HiddenInput />
                        <Checkbox.Control />
                        <Checkbox.Label>
                          Set <strong>all</strong>{" "}
                          {inactiveRegistrations.length} Path(s) aside?
                        </Checkbox.Label>
                      </Checkbox.Root>
                      <Stack pl={6} mt={1} gap={1}>
                        {inactiveRegistrations.map((registration) => (
                          <Fragment key={registration.id}>
                            <Field name={registration.id} as="div">
                              <Checkbox.Root
                                checked={!!form.values[registration.id]}
                                onCheckedChange={(e) =>
                                  form.setFieldValue(
                                    `${registration.id}`,
                                    e.checked
                                  )
                                }
                                colorPalette="whiteAlpha"
                                variant="outline"
                                size="sm"
                              >
                                <Checkbox.HiddenInput />
                                <Checkbox.Control />
                                <Checkbox.Label>
                                  {registration.group?.is_source_cohort ? (
                                    <strong>
                                      {registration.resource?.name}
                                    </strong>
                                  ) : (
                                    <>
                                      <strong>
                                        {registration.resource?.name}
                                      </strong>
                                      : {registration.group?.name}
                                    </>
                                  )}
                                </Checkbox.Label>
                              </Checkbox.Root>
                            </Field>
                            {/* Register the soft archive mutation for this registration. */}
                            <UseSoftArchiveRegistration
                              registration={registration}
                              mutationsRef={mutationsRef}
                            />
                          </Fragment>
                        ))}
                      </Stack>
                    </Box>
                    <Button
                      type="submit"
                      colorPalette="whiteAlpha"
                      loading={form.isSubmitting}
                      disabled={!checkedCount}
                    >
                      <Pathicon
                        icon="sidebar-bottom"
                        transform="rotate(-90deg)"
                      />
                      Set aside {checkedCount} Path(s)
                    </Button>
                  </VStack>
                </Form>
              )
            }}
          </Formik>
        )
      }}
      icon={
        <Pathicon
          icon="sidebar-bottom"
          transform="rotate(-90deg)"
          size="1.4em"
        />
      }
      layout="dismissable"
      colorPalette="dark"
    />
  )
}

export function HomeSetAsidePathsTipContainer({
  registrations
}: {
  registrations: HomeRegistrationFragment[]
}) {
  const inactiveRegistrations = useInactiveRegistrations(registrations)
  return inactiveRegistrations.length ? (
    <HomeSetAsidePathsTip inactiveRegistrations={inactiveRegistrations} />
  ) : null
}

export default HomeSetAsidePathsTip
